import { Card } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isBillingPlatformApiErrorResponse } from '@monetize/types/common';
import { pluralize } from '@monetize/utils/core';
import { isAxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  useCustomerPortalSessionActivate,
  useCustomerPortalSessionInit,
} from '../../../../api/customerPortalService';
import {
  MBox,
  MCard,
  MFlex,
  MFormField,
  MInput,
  MText,
} from '../../../../components/Monetize';
import MButton from '../../../../components/Monetize/MButton';
import { CustomerPortalFooter } from './CustomerPortalFooter';

type AuthStep = 'email' | 'code';

const EmailFormSchema = z.object({
  email: z
    .email('Please enter a valid email address')
    .min(1, 'Please enter your email address'),
});
type EmailFormValues = z.infer<typeof EmailFormSchema>;

const CodeFormSchema = z.object({
  code: z.string().min(1, 'Please enter the verification code'),
});
type CodeFormValues = z.infer<typeof CodeFormSchema>;

interface PendingSession {
  billingPortalSessionId: string;
  email: string;
  expiresAt: number; // timestamp in ms
}

function getPendingSessionStorageKey(
  tenantId: string,
  shareSecret: string,
): string {
  return `mn:customerPortal:${tenantId}:${shareSecret}:pendingSession`;
}

function loadPendingSession(
  tenantId: string,
  shareSecret: string,
): PendingSession | null {
  try {
    const key = getPendingSessionStorageKey(tenantId, shareSecret);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }

    const session = JSON.parse(stored) as PendingSession;
    if (session.expiresAt <= Date.now()) {
      localStorage.removeItem(key);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

function savePendingSession(
  tenantId: string,
  shareSecret: string,
  session: PendingSession,
): void {
  const key = getPendingSessionStorageKey(tenantId, shareSecret);
  localStorage.setItem(key, JSON.stringify(session));
}

function clearPendingSession(tenantId: string, shareSecret: string): void {
  const key = getPendingSessionStorageKey(tenantId, shareSecret);
  localStorage.removeItem(key);
}

interface EmailStepFormProps {
  onSubmit: (email: string) => void;
  defaultEmail: string;
  apiError: string;
  isLoading: boolean;
}

const EmailStepForm = ({
  onSubmit,
  defaultEmail,
  apiError,
  isLoading,
}: EmailStepFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormValues>({
    resolver: zodResolver(EmailFormSchema),
    mode: 'onSubmit',
    defaultValues: { email: defaultEmail },
  });

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data.email))}>
      <MFormField
        error={errors.email || (apiError ? { message: apiError } : undefined)}
        label="Email"
        mb={5}
      >
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <MInput
              {...field}
              type="email"
              placeholder="you@company.com"
              size="md"
              bg="white"
              disabled={isLoading}
            />
          )}
        />
      </MFormField>

      <MButton
        type="submit"
        w="100%"
        isLoading={isLoading}
        disabled={isLoading}
      >
        Continue
      </MButton>
    </form>
  );
};

interface CodeStepFormProps {
  onSubmit: (code: string) => void;
  onResendCode: () => void;
  onStartOver: () => void;
  minutesRemaining: number;
  apiError: string;
  isLoading: boolean;
}

const CodeStepForm = ({
  onSubmit,
  onResendCode,
  onStartOver,
  minutesRemaining,
  apiError,
  isLoading,
}: CodeStepFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CodeFormValues>({
    resolver: zodResolver(CodeFormSchema),
    mode: 'onSubmit',
    defaultValues: { code: '' },
  });

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data.code))}>
      <MFormField
        error={errors.code || (apiError ? { message: apiError } : undefined)}
        label="Verification code"
        mb={4}
      >
        <Controller
          name="code"
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <MInput
              {...rest}
              onChange={(e) =>
                onChange(e.target.value.toUpperCase().slice(0, 8))
              }
              type="text"
              placeholder="Enter code"
              size="md"
              bg="white"
              letterSpacing="0.1em"
              disabled={isLoading}
              autoFocus
            />
          )}
        />
      </MFormField>

      <MText color="gray.500" fontSize="xs" mb={5}>
        Code expires in {minutesRemaining}{' '}
        {pluralize('minute', minutesRemaining)}.
      </MText>

      <MButton
        type="submit"
        w="100%"
        isLoading={isLoading}
        disabled={isLoading}
        mb={4}
      >
        Verify
      </MButton>

      <MFlex justifyContent="space-between" gap={3}>
        <MButton
          variant="link"
          size="sm"
          color="gray.500"
          fontWeight="normal"
          onClick={onResendCode}
          disabled={isLoading}
        >
          Resend code
        </MButton>
        <MButton
          variant="link"
          size="sm"
          color="gray.500"
          fontWeight="normal"
          onClick={onStartOver}
          disabled={isLoading}
        >
          Start over
        </MButton>
      </MFlex>
    </form>
  );
};

interface CustomerPortalAuthProps {
  tenantId: string;
  shareSecret: string;
  onAuthSuccess: (
    sessionId: string,
    accessToken: string,
    accountId: string,
    billGroupId: string,
    email: string,
  ) => void;
}

export const CustomerPortalAuth = ({
  tenantId,
  shareSecret,
  onAuthSuccess,
}: CustomerPortalAuthProps) => {
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(
    () => loadPendingSession(tenantId, shareSecret),
  );

  const step: AuthStep = pendingSession ? 'code' : 'email';

  const [apiError, setApiError] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState(
    pendingSession?.email ?? '',
  );

  // Calculate minutes remaining until expiration
  const [minutesRemaining, setMinutesRemaining] = useState(() => {
    if (!pendingSession) {
      return 0;
    }
    return Math.max(
      0,
      Math.ceil((pendingSession.expiresAt - Date.now()) / 60000),
    );
  });

  // Update minutes remaining every minute
  useEffect(() => {
    if (!pendingSession) {
      return;
    }

    const interval = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((pendingSession.expiresAt - Date.now()) / 60000),
      );
      setMinutesRemaining(remaining);

      if (remaining <= 0) {
        clearPendingSession(tenantId, shareSecret);
        setPendingSession(null);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [pendingSession, tenantId, shareSecret]);

  function getInitErrorMessage(ex: unknown) {
    const response = isAxiosError(ex) ? ex.response?.data : ex;
    if (!isBillingPlatformApiErrorResponse(response)) {
      return 'Unable to send verification code. Please try again.';
    }
    switch (response.error.errorKey) {
      case 'ineligibleEmail':
        return "If this email address is eligible, you'll receive a verification code shortly.";
      case 'invalidPortalSessionShareRecord':
      case 'expiredPortalSessionShareRecord':
      case 'revokedPortalSessionLink':
        return 'This link is no longer valid. Please request a new link.';
      case 'invalidTenant':
        return 'The provided tenant ID is invalid. Please check your link and try again.';
      case 'billingPortalNotEnabled':
        return 'Billing portal access is not enabled for this tenant. Please contact support.';
      default:
        return 'Unable to send verification code. Please try again.';
    }
  }

  const { mutate: initSession, isPending: isInitPending } =
    useCustomerPortalSessionInit({
      onSuccess: (data) => {
        const expiresAt =
          Date.now() + data.minutesUntilChallengeExpiration * 60000;
        const session: PendingSession = {
          billingPortalSessionId: data.id,
          email: submittedEmail,
          expiresAt,
        };
        savePendingSession(tenantId, shareSecret, session);
        setPendingSession(session);
        setMinutesRemaining(data.minutesUntilChallengeExpiration);
        setApiError('');
      },
      onError: (ex) => {
        setApiError(getInitErrorMessage(ex));
      },
    });

  const { mutate: activateSession, isPending: isActivatePending } =
    useCustomerPortalSessionActivate({
      onSuccess: (data) => {
        const email = pendingSession?.email ?? submittedEmail;
        clearPendingSession(tenantId, shareSecret);
        onAuthSuccess(
          data.billingPortalSession.id,
          data.accessToken,
          data.accountId,
          data.billGroupId,
          email,
        );
      },
      onError: () => {
        setApiError('Invalid or expired code. Please try again.');
      },
    });

  const handleEmailSubmit = (email: string) => {
    setApiError('');
    setSubmittedEmail(email);
    initSession({ tenantId, shareSecret, email });
  };

  const handleCodeSubmit = (code: string) => {
    setApiError('');

    if (!pendingSession) {
      setApiError('Session expired. Please start over.');
      return;
    }

    activateSession({
      tenantId,
      billingPortalSessionId: pendingSession.billingPortalSessionId,
      challengeCode: code,
    });
  };

  const handleResendCode = () => {
    if (!pendingSession) {
      return;
    }
    setApiError('');
    initSession({
      tenantId,
      shareSecret,
      email: pendingSession.email,
    });
  };

  const handleStartOver = () => {
    clearPendingSession(tenantId, shareSecret);
    setPendingSession(null);
    setApiError('');
  };

  const isLoading = isInitPending || isActivatePending;

  return (
    <MBox minH="100vh" display="flex" flexDirection="column">
      <MFlex flex={1} alignItems="center" justifyContent="center" px={4} py={8}>
        <MCard
          variant="borderless"
          shadow="md"
          borderRadius="lg"
          maxW="400px"
          w="100%"
        >
          <Card.Body p={8}>
            <MText
              fontSize="xl"
              fontWeight="600"
              color="gray.800"
              mb={1}
              textAlign="center"
            >
              {step === 'email' ? 'Sign in' : 'Enter verification code'}
            </MText>
            <MText color="gray.500" fontSize="sm" mb={6} textAlign="center">
              {step === 'email'
                ? 'Enter your email to access your customer portal'
                : `We sent a code to ${pendingSession?.email}`}
            </MText>

            {step === 'email' && (
              <EmailStepForm
                onSubmit={handleEmailSubmit}
                defaultEmail={pendingSession?.email ?? ''}
                apiError={apiError}
                isLoading={isLoading}
              />
            )}
            {step === 'code' && (
              <CodeStepForm
                onSubmit={handleCodeSubmit}
                onResendCode={handleResendCode}
                onStartOver={handleStartOver}
                minutesRemaining={minutesRemaining}
                apiError={apiError}
                isLoading={isLoading}
              />
            )}
          </Card.Body>
        </MCard>
      </MFlex>

      <CustomerPortalFooter />
    </MBox>
  );
};
